import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from '../lib/useMediaQuery';
import { MOBILE_QUERY } from '../lib/breakpoints';
// Page-scroll lock is shared (ref-counted) across all overlay surfaces — see
// lib/scrollLock — so a stacked Wizard/BottomSheet keeps one lock and neither
// unlocks the page while the other is still open.
import { lockPageScroll, unlockPageScroll } from '../lib/scrollLock';

// ── Resizable (mobile) sheet height math ──────────────────────────────────────
// Heights are in px, derived from the viewport so they track device size and the
// safe-area inset. The initial height opens the sheet at a comfortable middle
// position; the grip drag clamps between MIN and MAX, and dragging below CLOSE
// (or a fast downward fling) dismisses it.
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
function resizableHeights() {
  const vh = window.innerHeight || 800;
  const min = Math.round(vh * 0.35); // ~35vh floor
  // ~92dvh ceiling. window.innerHeight already excludes browser chrome; the 8%
  // gap below the top + the panel's own safe-area padding keep MAX off the home
  // bar without needing to read env(safe-area-inset-bottom) from JS.
  const max = Math.round(vh * 0.92);
  const initial = clamp(Math.min(Math.round(vh * 0.6), 560), min, max); // min(60vh, 560px)
  const close = Math.round(vh * 0.25); // below this on release → dismiss
  return { min, max, initial, close };
}

/** Responsive modal: a centered dialog on desktop, a slide-up bottom sheet on
 *  mobile (see .sheet-* in responsive.css). Close via the ✕ button only — a
 *  backdrop click does NOT close, so an accidental click outside can't discard work.
 *
 *  size='full' makes it fill the screen on mobile (100dvh) with an independently
 *  scrolling body and a pinned header/footer — for tall forms (e.g. the reply
 *  editor) that would otherwise overflow a 90vh sheet and become unusable on a
 *  phone. Desktop stays a centered dialog (taller, with a pinned footer).
 *
 *  resizable=true (mobile only) opens the sheet at a FIXED initial height that
 *  does NOT size to content — the body scrolls internally, header/footer pin — and
 *  turns the grip into a drag handle to resize it taller/shorter (drag down past a
 *  threshold closes). Desktop is unaffected: it stays a centered dialog. */
export function BottomSheet({ open, onClose, title, children, width, padded = true, size = 'default', maximized = false, footer, overlay, resizable = false }: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  width?: number;
  /** Set false when the caller manages its own inner padding. */
  padded?: boolean;
  /** 'full' = fill the screen on mobile with a scrolling body + pinned header/footer. */
  size?: 'default' | 'full';
  /** Fixed near-fullscreen dialog (90vw × 90dvh) on desktop with a scrolling body +
   *  pinned header — so its content can't resize the panel (e.g. an editor whose side
   *  panel changes height per selection, which would otherwise make the dialog jump).
   *  Falls back to the full-screen sheet on mobile. Defaults false. */
  maximized?: boolean;
  /** Sticky action bar pinned to the bottom (stays reachable while the body scrolls). */
  footer?: ReactNode;
  /** Absolutely-positioned layer covering the inner body region only — the header
   *  (with ✕) and footer (actions) stay uncovered and clickable. The node should
   *  use position: absolute; inset: 0. E.g. a busy/loading overlay. */
  overlay?: ReactNode;
  /** Opt-in: on mobile, open at a fixed initial height (body scrolls internally)
   *  with a draggable grip to resize taller/shorter; drag down to close. Desktop
   *  (centered dialog) is unchanged. Defaults false → existing callers untouched. */
  resizable?: boolean;
}) {
  // While open: lock the page scroll behind the dialog, and close on Escape.
  // onClose is read through a ref so an inline handler's identity change doesn't
  // re-run this effect (which would churn the scroll lock).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    lockPageScroll();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A TinyMCE dialog (Insert link, Source code) portals itself to <body>, so its
      // Escape isn't contained by the sheet — and the silver theme closes it on
      // KEYUP (themes/silver: getKeyupRules has the ESCAPE rule, getKeydownRules
      // doesn't). Our keydown listener would therefore fire first and tear the whole
      // sheet down, unmounting the editor and discarding whatever was typed. Let the
      // dialog take this Escape; the next one closes the sheet.
      if (document.querySelector('.tox-dialog')) return;
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockPageScroll();
    };
  }, [open]);

  // Only treat resizable as active when on the compact (bottom-sheet) breakpoint —
  // the same media context responsive.css uses to render .sheet-panel as a sheet.
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const dragResize = resizable && isMobile;

  // Fixed managed height (px) for the resizable sheet. null until measured /
  // on desktop, where height stays content-driven (centered dialog).
  const [sheetHeight, setSheetHeight] = useState<number | null>(null);
  // Drag bookkeeping lives in a ref so pointermove doesn't re-render per frame.
  const dragRef = useRef<{ startY: number; startH: number; lastY: number; lastT: number; vy: number } | null>(null);

  // Seed the initial height when the resizable sheet opens (and reset on close so
  // a re-open starts fresh). Re-seed if the breakpoint flips into mobile.
  useEffect(() => {
    if (open && dragResize) {
      setSheetHeight((h) => h ?? resizableHeights().initial);
    } else {
      setSheetHeight(null);
    }
  }, [open, dragResize]);

  if (!open) return null;
  const full = size === 'full' || maximized;
  // The panel pins header/footer + scrolls its body internally when it's a full
  // sheet OR a (mobile) resizable one — both are flex columns with a fixed height.
  const flexColumn = full || (dragResize && sheetHeight != null);

  function onGripPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragResize) return;
    const { min, max } = resizableHeights();
    const startH = clamp(sheetHeight ?? resizableHeights().initial, min, max);
    dragRef.current = { startY: e.clientY, startH, lastY: e.clientY, lastT: e.timeStamp, vy: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  }
  function onGripPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const d = dragRef.current;
    if (!d) return;
    const { min, max } = resizableHeights();
    // Dragging UP (smaller clientY) grows the sheet; DOWN shrinks it.
    const next = clamp(d.startH + (d.startY - e.clientY), min, max);
    // Track instantaneous downward velocity (px/ms) for fling-to-close.
    const dt = e.timeStamp - d.lastT;
    if (dt > 0) d.vy = (e.clientY - d.lastY) / dt;
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    setSheetHeight(next);
  }
  function onGripPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    const { close } = resizableHeights();
    const flungDown = d.vy > 1.2; // fast downward fling
    if ((sheetHeight != null && sheetHeight <= close) || flungDown) {
      onClose();
    }
  }

  // Portal to <body> so the fixed-position overlay is always viewport-relative.
  // Rendered inline, an ancestor with backdrop-filter/transform/contain (e.g. the
  // .info-panel glass card) becomes its containing block and traps it in the
  // sidebar — the dialog must escape that to fill the screen.
  const panelStyle: CSSProperties = {};
  if (width) panelStyle.width = width;
  if (dragResize && sheetHeight != null) {
    // Fixed height, internal scroll: the panel never sizes to content.
    panelStyle.height = sheetHeight;
    panelStyle.maxHeight = '92dvh';
    panelStyle.display = 'flex';
    panelStyle.flexDirection = 'column';
    panelStyle.overflow = 'hidden';
  }
  if (maximized && !isMobile) {
    // Desktop: a fixed 90vw × 90dvh dialog. Fixed dimensions mean the content
    // (e.g. a properties panel whose height changes per selection) can't resize the
    // panel — no layout jump — and a canvas stage keeps a stable width/scale so drag
    // stays aligned with the cursor. (--full gives the flex-column scrolling body;
    // on mobile the --full media rule fills the screen instead, so no inline size.)
    panelStyle.width = '90vw';
    panelStyle.height = '90dvh';
    panelStyle.maxHeight = '90dvh';
  }

  return createPortal(
    <div className="sheet-backdrop">
      <div
        className={`sheet-panel${full ? ' sheet-panel--full' : ''}`}
        style={Object.keys(panelStyle).length ? panelStyle : undefined}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`sheet-grip${dragResize ? ' sheet-grip--draggable' : ''}`}
          role={dragResize ? 'separator' : undefined}
          aria-label={dragResize ? 'Drag to resize' : undefined}
          aria-orientation={dragResize ? 'horizontal' : undefined}
          onPointerDown={dragResize ? onGripPointerDown : undefined}
          onPointerMove={dragResize ? onGripPointerMove : undefined}
          onPointerUp={dragResize ? onGripPointerUp : undefined}
          onPointerCancel={dragResize ? onGripPointerUp : undefined}
        />
        <div
          className="sheet-header"
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border-subtle)',
            // A full-height (or resizable) sheet pins the header via flex layout;
            // the default sheet scrolls as one piece, so the header sticks instead.
            position: flexColumn ? 'static' : 'sticky', top: 0, background: 'var(--bg-elevated)', zIndex: 1, flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 'var(--text-base)' }}>{title}</div>
          <button
            className="btn-icon"
            aria-label="Close"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16 }}
          >
            ✕
          </button>
        </div>
        {/* Body region: positioning context for an optional overlay that covers
            only the inner content — the header (✕) and footer (actions) stay free. */}
        <div
          className="sheet-body-region"
          style={{
            position: 'relative',
            ...(flexColumn ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
          }}
        >
          <div
            className="sheet-body"
            style={{
              ...(padded ? { padding: 'var(--space-4)' } : {}),
              // A resizable sheet has a fixed height, so the body must scroll
              // internally (the --full CSS only kicks in for size="full").
              ...(dragResize && sheetHeight != null
                ? { flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }
                : {}),
            }}
          >
            {children}
          </div>
          {overlay}
        </div>
        {footer && (
          <div
            className="sheet-footer"
            style={{
              flexShrink: 0, background: 'var(--bg-elevated)', borderTop: '1px solid var(--border-subtle)',
              padding: 'var(--space-3) var(--space-4)',
              paddingBottom: 'calc(var(--space-3) + env(safe-area-inset-bottom))',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
